import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { bordadoElegido, contextoCamisa, cop, errorApi, faltaDatoComprador, faltaEnGrupo, gruposVisibles, TiendaApi } from './tiendaUtils';
import Progreso from './Progreso';
import PasoGrupo from './PasoGrupo';
import PasoPuntos from './PasoPuntos';
import PasoDatosInvitado, { COMPRADOR_VACIO } from './PasoDatosInvitado';
import PasoRevisar from './PasoRevisar';

const armarPasos = (grupos, invitado) => [
  ...grupos.map(g => ({ id: g.id, titulo: g.nombre, grupo: g })),
  invitado ? { id: 'datos', titulo: 'Tus datos' } : { id: 'puntos', titulo: 'Puntos JPM' },
  { id: 'revisar', titulo: 'Revisar y pagar' },
];

const seleccionDe = (g, valor, mangaCorta) => {
  const base = { grupo_id: g.id, opcion_ids: valor?.opcion_ids || [], texto: valor?.texto || '' };
  if (!g.bordado || !base.texto) return base;
  const { ubicacion, color } = bordadoElegido(g, valor, mangaCorta);
  return { ...base, ubicacion: ubicacion?.id || '', color_hilo: color?.nombre || '' };
};

const cuerpoPedido = ({ productoId, grupos, sel, envio, medidasModo, invitado, puntos, comprador }) => {
  const { mangaCorta } = contextoCamisa(grupos, sel);
  const { sitio_web: sitioWeb, ...datosComprador } = comprador;
  return {
    producto_id: productoId,
    selecciones: grupos.map(g => seleccionDe(g, sel[g.id], mangaCorta)),
    envio_id: envio.id,
    envio_direccion: envio.direccion,
    notas: envio.notas,
    medidas_coordinacion: medidasModo || null,
    ...(invitado ? { comprador: datosComprador, sitio_web: sitioWeb } : { puntos_a_usar: puntos }),
  };
};

export default function PersonalizarProducto({ productoId, onVolver, onCreado }) {
  const api = useContext(TiendaApi);
  const [datos, setDatos] = useState(null);
  const [sel, setSel] = useState({});
  const [puntos, setPuntos] = useState(0);
  const [envio, setEnvio] = useState({ id: '', direccion: '', notas: '' });
  const [comprador, setComprador] = useState(COMPRADOR_VACIO);
  const [medidasModo, setMedidasModo] = useState('');
  const [paso, setPaso] = useState(0);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.producto(productoId)
      .then(r => { setDatos(r.data); setEnvio(e => ({ ...e, id: r.data.envio_opciones[0]?.id || '' })); })
      .catch(e => { toast.error(errorApi(e, 'Este producto no está disponible')); onVolver(); });
  }, [api, productoId, onVolver]);

  if (!datos) return <div className="h-72 animate-pulse rounded-2xl bg-[#18181B]" data-testid="tienda-cargando" />;

  const { producto } = datos;
  const grupos = gruposVisibles(producto.grupos, sel);
  const pasos = armarPasos(grupos, api.invitado);
  const indice = Math.min(paso, pasos.length - 1);
  const actual = pasos[indice];
  const ir = (n) => { setPaso(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const errorDatos = () => (api.invitado ? faltaDatoComprador(comprador) : null);

  const continuar = () => {
    if (actual.grupo && faltaEnGrupo(actual.grupo, sel[actual.id])) {
      toast.error(actual.grupo.tipo === 'texto' ? `Completa «${actual.titulo}»` : `Elige una opción en «${actual.titulo}»`);
      return;
    }
    if (actual.id === 'datos' && errorDatos()) { toast.error(errorDatos()); return; }
    ir(indice + 1);
  };

  const confirmar = async () => {
    const falta = grupos.findIndex(g => faltaEnGrupo(g, sel[g.id]));
    if (falta >= 0) { toast.error(`Completa «${grupos[falta].nombre}»`); ir(falta); return; }
    if (errorDatos()) { toast.error(errorDatos()); ir(grupos.length); return; }
    if (!datos.medidas.registradas && !medidasModo) { toast.error('Elige cómo deseas coordinar la toma de medidas'); return; }
    setEnviando(true);
    try {
      const r = await api.crearPedido(cuerpoPedido({ productoId: producto.id, grupos, sel, envio, medidasModo, invitado: api.invitado, puntos, comprador }));
      onCreado(r.data);
    } catch (e) {
      toast.error(errorApi(e, 'No se pudo crear tu pedido. Intenta de nuevo'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="mx-auto max-w-3xl space-y-5" data-testid="tienda-personalizar">
      <div className="flex items-start gap-3">
        <button onClick={indice === 0 ? onVolver : () => ir(indice - 1)} className="rounded-xl border border-[#27272A] p-2.5 text-zinc-300 transition-colors hover:border-[#C9A962]/50 hover:text-white" aria-label="Volver" data-testid="tienda-atras">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h2 className="font-serif text-xl font-medium text-white">{producto.nombre}</h2>
          <p className="text-sm text-[#C9A962]">{cop(producto.precio)} COP · {producto.precio_nota}</p>
        </div>
      </div>
      <Progreso pasos={pasos} actual={indice} onIr={ir} />
      {actual.grupo && <PasoGrupo grupo={actual.grupo} valor={sel[actual.id]} contexto={contextoCamisa(producto.grupos, sel)} onCambiar={v => setSel(s => ({ ...s, [actual.id]: v }))} />}
      {actual.id === 'puntos' && <PasoPuntos info={datos.puntos} precio={producto.precio} puntos={puntos} setPuntos={setPuntos} />}
      {actual.id === 'datos' && <PasoDatosInvitado comprador={comprador} setComprador={setComprador} />}
      {actual.id === 'revisar' && (
        <PasoRevisar datos={datos} grupos={grupos} sel={sel} puntos={puntos} envio={envio} setEnvio={setEnvio}
          medidasModo={medidasModo} setMedidasModo={setMedidasModo} comprador={api.invitado ? comprador : null}
          onEditar={ir} enviando={enviando} onConfirmar={confirmar} />
      )}
      {actual.id !== 'revisar' && (
        <button onClick={continuar} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#C9A962] py-3.5 font-semibold text-black transition-colors hover:bg-[#D4B872]" data-testid="tienda-continuar">
          {pasos[indice + 1].id === 'revisar' ? 'Revisar mi pedido' : 'Continuar'}<ChevronRight className="h-4 w-4" />
        </button>
      )}
    </section>
  );
}
